/* Data layer for the new fresh road UI.
   Reuses the existing axios instances (token attach + 401 redirect) from services/api.
   Domain data lives on the agent API (3016 Sales.js); auth/groups on 3015. */

import { agentApi, phonenumbersAPI, userDashboardAPI, agentsAPI, templatesAPI } from '../../services/api';

export { phonenumbersAPI, agentsAPI, templatesAPI };

export const scriptsAPI = {
  list: () => agentApi.get('/api/scripts'),
  get: (id) => agentApi.get(`/api/script/${id}`),
  save: (data) => agentApi.post('/api/save-script', data),
  update: (data) => agentApi.post('/api/update-script', data),
  remove: (_id) => agentApi.post('/api/delete-script', { _id }),
};

export const logsAPI = {
  list: () => agentApi.get('/api/agentlogs'),
  usage: (params) => userDashboardAPI.getUsage(params),
};

/* Test a script in-editor: same /api/chatbot endpoint the legacy "Test Agent" tab uses. */
export const chatbotAPI = {
  send: (body) => agentApi.post('/api/chatbot', body),
};

export const knowledgeAPI = {
  collections: () => agentApi.get('/api/knowledgebase-collections'),
  files: (collectionId) =>
    agentApi.get('/api/knowledgebase', { params: collectionId ? { collectionId } : {} }),
  /* Upload documents (multipart). formData: `files` (one or more) + `collectionName` (new)
     or `collectionId` (existing). Mirrors the legacy UploadKnowledgeBase flow. */
  upload: (formData, ownerId) =>
    agentApi.post('/api/upload-docs', formData, {
      params: ownerId ? { ownerId } : {},
      timeout: 120000,
    }),
};

/* ---------- helpers ---------- */

/** Backend script.type is a display string; map to the UI's short kind. */
export const scriptKind = (type) => (type === 'Conversation Flow' ? 'Workflow' : 'Prompt');

/** agentlogs rows often lack `duration`; mirror backend effectiveCallDurationMs. */
export const effectiveDurationMs = (log) => {
  if (typeof log?.duration === 'number' && log.duration > 0) return log.duration;
  const start = log?.callStartTime ? new Date(log.callStartTime).getTime() : null;
  const end = log?.callEndTime ? new Date(log.callEndTime).getTime() : null;
  if (start && end && end > start) return end - start;
  return 0;
};

export const formatDuration = (ms) => {
  if (!ms) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

export const timeAgo = (date) => {
  if (!date) return '—';
  const diff = Date.now() - new Date(date).getTime();
  if (diff < 0) return 'now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(date).toLocaleDateString();
};

/** Normalize a transcript field that may be a string or an array of lines/objects. */
export const transcriptLines = (log) => {
  if (Array.isArray(log?.messages) && log.messages.length) {
    return log.messages
      .filter((m) => m && m.content)
      .map((m) => ({ from: m.role === 'user' ? 'user' : 'ai', text: String(m.content) }));
  }
  const t = log?.transcript;
  if (!t) return [];
  const lines = Array.isArray(t) ? t.map((x) => (typeof x === 'string' ? x : x?.content || '')) : String(t).split('\n');
  return lines
    .filter(Boolean)
    .map((line) => {
      const m = line.match(/^\s*(AI|Agent|Assistant|Bot|User|Caller|Customer|Human)\s*:\s*(.*)$/i);
      if (m) {
        const who = m[1].toLowerCase();
        return { from: ['user', 'caller', 'customer', 'human'].includes(who) ? 'user' : 'ai', text: m[2] };
      }
      return { from: 'ai', text: line };
    });
};

/** Channel bucket from a log's callType. */
export const logChannel = (log) => {
  const t = (log?.callType || '').toLowerCase();
  if (t.includes('chat')) return 'chat';
  if (t.includes('sms')) return 'sms';
  if (t.includes('email')) return 'email';
  return 'phone';
};

/** Status label + tone from analysis outcome. */
export const logStatus = (log) => {
  const o = (log?.outcome || '').toLowerCase();
  if (o === 'success') return { label: 'Completed', tone: 'green' };
  if (o === 'failure') return { label: 'Failed', tone: 'red' };
  if (log?.callEndTime || log?.transcript) return { label: 'Ended', tone: 'gray' };
  return { label: 'In Progress', tone: 'blue' };
};
